import clsx from 'clsx';
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import {
  IconEye,
  IconTrash,
  IconXboxX,
  IconSearch,
  IconMoodKid,
  IconLockOpen,
  IconMoodCheck,
  IconClockHour5,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Input from '@/components/Input/Input';
import { SegmentedControl } from '@/components/SegmentedControl';
import Select from '@/components/Select';
import SkeletonLoader from '@/components/SkeletonLoader';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import {
  CommonStatusConfig,
  CHILDREN_STATUS_OPTIONS,
} from '@/constants/constant';
import { ECommonStatus, EPersonalSubscriptionType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getChildrenPersonalSubscriberList,
  getChildrenPersonalSubscriberSummary,
} from '@/service/childrenService';
import useCommonStore from '@/store/useCommonStore';
import {
  IStatsDataItem,
  ISelectedRowValue,
  ISubscriptionChildDetails,
} from '@/types';
import {
  pickBy,
  getAssetUrl,
  customPredicate,
  formatLastActive,
  getStartOfMonthUnix,
  formatSecondsToDuration,
} from '@/utils';

const PersonalSubscriber = forwardRef(
  (
    {
      setSelectedChildren,
      setIsOpenBlockModal,
      setIsRemoveChildrenModalOpen,
      handleBlockChildren,
      setIsChildDetailsModalOpen,
    }: {
      setSelectedChildren: (value: ISelectedRowValue | null) => void;
      setIsOpenBlockModal: (value: boolean) => void;
      setIsRemoveChildrenModalOpen: (value: boolean) => void;
      handleBlockChildren: (children: ISubscriptionChildDetails) => void;
      isChildDetailsModalOpen: boolean;
      setIsChildDetailsModalOpen: (value: boolean) => void;
    },
    ref
  ) => {
    const { setTitle, setIsBreadcrumb } = usePageTitle();
    const { formatText } = useFormatText();
    const [search, setSearch] = useState('');
    const [childStatus, setChildStatus] = useState<string>('');
    const debouncedSearch = useDebounce(search?.trim(), 500);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [personalSubscriptionType, setPersonalSubscriptionType] =
      useState<EPersonalSubscriptionType>(EPersonalSubscriptionType.FREE);
    const [childrens, setChildrens] = useState<ISubscriptionChildDetails[]>([]);
    const { loadingTable, setLoadingTable, loadingSummary, setLoadingSummary } =
      useCommonStore();

    const SUMMARY_CONFIG = [
      {
        key: 'freeChildren',
        icon: <IconMoodKid color="#717784" />,
        label:
          'children.personal-subscriber.summary-cards.free-account-children',
        isShow: personalSubscriptionType === EPersonalSubscriptionType.FREE,
      },
      {
        key: 'paidChildren',
        icon: <IconMoodKid color="#717784" />,
        label:
          'children.personal-subscriber.summary-cards.paid-account-children',
        isShow: personalSubscriptionType === EPersonalSubscriptionType.PAID,
      },
      {
        key: 'activeStudent',
        icon: <IconMoodCheck color="#717784" />,
        label: 'children.personal-subscriber.summary-cards.active-children',
        isShow: true,
      },
      {
        key: 'averageScreenTime',
        icon: <IconClockHour5 color="#717784" />,
        label: 'children.personal-subscriber.summary-cards.average-screen-time',
        isShow: true,
      },
    ];

    const [statsData, setStatsData] = useState<IStatsDataItem[]>(
      SUMMARY_CONFIG.map((item) => ({
        ...item,
        value: '0',
        trend: '0%',
        trendLabel: 'common.this-month',
        trendDirection: 'STABLE',
      }))
    );

    const pageTitle = formatText('children.title');
    const userManagement = formatText('user-management.title');

    useEffect(() => {
      setCurrentPage(1);
    }, [debouncedSearch, childStatus]);

    useEffect(() => {
      document.title = pageTitle;
      setTitle(pageTitle);
      setIsBreadcrumb([{ label: userManagement }, { label: pageTitle }]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageTitle, userManagement]);

    const fetchChildrens = async () => {
      setLoadingTable(true);
      const params = pickBy(
        {
          search: debouncedSearch || undefined,
          status: childStatus,
          subscribe_type: personalSubscriptionType ?? 'free',
          offset: (currentPage - 1) * pageSize,
          limit: pageSize,
          order: sortField ? { [sortField]: sortDirection } : undefined,
        },
        customPredicate
      );
      const response = await getChildrenPersonalSubscriberList(params);

      if (response?.status) {
        // Use API data if available, otherwise use dummy data for testing
        const apiData = response?.data || [];

        if (apiData?.length > 0) {
          setChildrens(apiData);
          setTotal(response?.total || 0);
        } else {
          setChildrens([]);
          setTotal(0);
        }
      } else {
        setChildrens([]);
        setTotal(0);
      }
      setLoadingTable(false);
    };

    const fetchSummary = async () => {
      setLoadingSummary(true);
      const monthTimestamp = getStartOfMonthUnix();

      const response = await getChildrenPersonalSubscriberSummary({
        month: monthTimestamp,
      });
      if (response?.status) {
        const apiData = response?.data;
        const mappedSummary = SUMMARY_CONFIG?.map((item) => {
          const itemData = apiData[item?.key as keyof typeof apiData];
          const value =
            item.key === 'averageScreenTime'
              ? formatSecondsToDuration(itemData?.total)
              : itemData?.total?.toLocaleString() || '0';
          return {
            ...item,
            value,
            trend: `${itemData?.growth || 0}%`,
            trendLabel: 'common.this-month',
            trendDirection: itemData?.trend || 'STABLE',
          };
        });
        setStatsData(mappedSummary);
      }
      setLoadingSummary(false);
    };

    useEffect(() => {
      fetchSummary();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personalSubscriptionType]);

    useEffect(() => {
      fetchChildrens();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      debouncedSearch,
      childStatus,
      currentPage,
      pageSize,
      sortField,
      sortDirection,
      personalSubscriptionType,
    ]);

    useImperativeHandle(ref, () => ({
      fetchChildrens,
      fetchSummary,
    }));

    const handleSort = (key: string) => {
      if (sortField === key) {
        setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
      } else {
        setSortField(key);
        setSortDirection('ASC');
      }
    };

    const columns = [
      {
        title: 'children.personal-subscriber.columns.name',
        key: 'children_name',
        width: '20%',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (row: ISubscriptionChildDetails) =>
          row?.name ? (
            <div className="flex items-center gap-2">
              <AvatarGroupList
                list={[
                  {
                    id: row.id,
                    name: row.name,
                    image: getAssetUrl(row?.asset),
                  },
                ]}
                size={40}
                avatarClassName="w-8 h-8 text-[10px]"
              />
              <span className="text-sm font-medium text-neutral-primary">
                {row?.name}
              </span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        title: 'children.personal-subscriber.columns.associated-parent',
        key: 'parent',
        width: '25%',
        sorter: true,
        handleSort: () => handleSort('parent_name'),
        render: (row: ISubscriptionChildDetails) => (
          <span className="text-sm font-medium text-neutral-primary">
            {row?.parent?.name || '-'}
          </span>
        ),
      },
      {
        title: 'children.personal-subscriber.columns.status',
        key: 'status',
        width: '15%',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (row: ISubscriptionChildDetails) => {
          const config =
            CommonStatusConfig[row?.status as keyof typeof CommonStatusConfig];
          return (
            <span
              className={clsx(
                'px-2 py-1 rounded-full text-xs font-semibold',
                config?.className
              )}
            >
              {formatText(config?.label)}
            </span>
          );
        },
      },
      {
        title: 'children.personal-subscriber.columns.last-active',
        key: 'last_active_at',
        width: '20%',
        sorter: true,
        handleSort: () => handleSort('last_active_at'),
        render: (row: ISubscriptionChildDetails) =>
          formatLastActive(Number(row?.last_active_at), formatText),
      },
      {
        title: 'children.personal-subscriber.columns.action',
        key: 'action',
        width: '5%',
        align: 'center',
        render: (row: ISubscriptionChildDetails) => {
          const isBlocked = row?.status === ECommonStatus.IN_ACTIVE;

          const actions = [
            {
              key: 'view',
              label: 'common.actions.view',
              icon: <IconEye size={18} />,
              onClick: () => {
                setSelectedChildren({
                  id: row?.id,
                  name: row?.name,
                  status: row?.status,
                });
                setIsChildDetailsModalOpen(true);
              },
            },
            {
              key: isBlocked ? 'unblock' : 'block',
              label: isBlocked
                ? 'common.actions.unblock'
                : 'common.actions.block',
              icon: isBlocked ? (
                <IconLockOpen size={18} />
              ) : (
                <IconXboxX size={18} />
              ),
              onClick: () => {
                if (isBlocked) {
                  handleBlockChildren(row);
                } else {
                  setSelectedChildren({
                    id: row?.id,
                    name: row?.name,
                    status: row?.status,
                  });
                  setIsOpenBlockModal(true);
                }
              },
            },
            {
              key: 'remove',
              label: 'common.actions.remove',
              icon: <IconTrash size={18} />,
              isDanger: true,
              onClick: () => {
                setSelectedChildren({
                  id: row?.id,
                  name: row?.name,
                  status: row?.status,
                });
                setIsRemoveChildrenModalOpen(true);
              },
            },
          ];

          return <ActionDropdown actions={actions} />;
        },
      },
    ];

    const isFilterApplied = childStatus !== '';

    const showListUI =
      loadingTable ||
      childrens?.length > 0 ||
      search?.length > 0 ||
      debouncedSearch?.length > 0 ||
      isFilterApplied;

    return (
      <>
        {showListUI && (
          <>
            <div className="flex flex-wrap justify-between items-center gap-2.5 mb-6 pl-1">
              <div className="flex items-center gap-2.5">
                <Input
                  name="search"
                  placeholder={formatText(
                    'children.personal-subscriber.search-placeholder'
                  )}
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearch(e.target.value)
                  }
                  leftIcon={{
                    icon: <IconSearch size={20} className="text-neutral-500" />,
                  }}
                  className="min-w-70"
                  inputClassName="py-[13px]"
                />
                <Select
                  placeholder={formatText(
                    'children.personal-subscriber.status-placeholder'
                  )}
                  options={CHILDREN_STATUS_OPTIONS}
                  selected={childStatus}
                  onChange={(value) => setChildStatus(value as string)}
                  className="w-37.5! py-3.25"
                  isTextFormat
                  isClearable
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {statsData
                ?.filter((stat) => stat?.isShow)
                ?.map((stat, index) => (
                  <SummaryCard
                    key={index}
                    variant="dashboard"
                    label={formatText(stat.label)}
                    value={stat?.value}
                    icon={stat?.icon}
                    trendDirection={stat?.trendDirection}
                    isPositive={stat?.isPositive}
                    trendLabel={
                      stat?.trendLabel ? formatText(stat.trendLabel) : undefined
                    }
                    trend={stat?.trend}
                    isLoading={loadingSummary}
                  />
                ))}
            </div>
          </>
        )}
        <div className="mt-6">
          {
            // showListUI &&
            loadingTable ? (
              <SkeletonLoader className="h-10 w-48 mb-6" variant="rect" />
            ) : (
              <SegmentedControl
                options={[
                  {
                    label: formatText('children.personal-subscriber.tab.free'),
                    value: EPersonalSubscriptionType.FREE,
                  },
                  // {
                  //   label: formatText('children.personal-subscriber.tab.paid'),
                  //   value: EPersonalSubscriptionType.PAID,
                  // },
                ]}
                value={personalSubscriptionType}
                onChange={setPersonalSubscriptionType}
                className="mb-6"
              />
            )
          }
          <CustomTable
            isLoading={loadingTable}
            dataSource={childrens}
            columns={columns}
            total={total}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            fieldName={sortField}
            sortDirection={sortDirection}
            rootClassName="h-[calc(100vh-740px)] lg:h-[calc(100vh-470px)]"
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            isEmpty={
              !loadingTable &&
              childrens?.length === 0 &&
              search?.length === 0 &&
              debouncedSearch?.length === 0 &&
              !isFilterApplied
            }
            emptyTitle={formatText(
              'children.personal-subscriber.not-found-title'
            )}
            emptyDescription={formatText(
              'children.personal-subscriber.not-found-desc'
            )}
            isEmptySearch={
              childrens?.length === 0 &&
              (search?.length > 0 ||
                debouncedSearch?.length > 0 ||
                isFilterApplied)
            }
            onRowClick={(id) => {
              const child = childrens.find((item) => item.id === id);
              if (child) {
                setSelectedChildren({
                  id: child?.id,
                  name: child?.name,
                  status: child?.status,
                });
                setIsChildDetailsModalOpen(true);
              }
            }}
          />
        </div>
      </>
    );
  }
);

export default PersonalSubscriber;
